import { useMemo } from 'react';

import type { Incident } from 'components/IAP/hooks/incidentHooks';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import type { AreaOfResponsibility } from 'types/aor';
import { useAorText } from 'utils/aorTerminology';

export type ReportsFilterKind = 'aor' | 'incident' | 'asset' | 'custom';

export type ReportsFilterSelection = {
  value: UUID;
  label: string;
  kind: ReportsFilterKind;
};

export type ReportsFilterOptionGroup = {
  label: string;
  options: ReportsFilterSelection[];
};

export type ReportsFilterAsset = {
  resource_set_id: UUID;
  name: string;
  archived?: boolean;
};

export type ReportsFilterCustomReport = Pick<
  GetSitrepsResponse,
  'id' | 'name' | 'subject_kind' | 'spatial_filter_label' | 'archived'
>;

type BuildReportsFilterGroupsArgs = {
  aors: AreaOfResponsibility[];
  incidents: Incident[];
  assets?: ReportsFilterAsset[];
  customReports?: ReportsFilterCustomReport[];
  aorsLabel: string;
};

/** The name a custom-area report goes by in the dropdown. */
export const customReportLabel = (report: ReportsFilterCustomReport) =>
  report.spatial_filter_label?.trim() || report.name || 'Custom area';

export function buildReportsFilterGroups({
  aors,
  incidents,
  assets = [],
  customReports = [],
  aorsLabel,
}: BuildReportsFilterGroupsArgs): ReportsFilterOptionGroup[] {
  const assetOptions = new Map<string, ReportsFilterSelection>();
  for (const asset of assets) {
    if (asset.archived || assetOptions.has(asset.resource_set_id)) continue;
    assetOptions.set(asset.resource_set_id, {
      value: asset.resource_set_id,
      label: asset.name,
      kind: 'asset',
    });
  }
  const customOptions = new Map<string, ReportsFilterSelection>();
  for (const report of customReports) {
    if (report.subject_kind !== 'custom' || report.archived) continue;
    if (customOptions.has(report.id)) continue;
    customOptions.set(report.id, {
      value: report.id,
      label: customReportLabel(report),
      kind: 'custom',
    });
  }
  const byLabel = (a: ReportsFilterSelection, b: ReportsFilterSelection) =>
    a.label.localeCompare(b.label);

  return [
    {
      label: aorsLabel,
      options: aors.map((aor) => ({
        value: aor.id,
        label: aor.name,
        kind: 'aor' as const,
      })),
    },
    {
      label: 'Incidents',
      options: incidents.map((incident) => ({
        value: incident.id,
        label: incident.name,
        kind: 'incident' as const,
      })),
    },
    { label: 'Assets', options: [...assetOptions.values()].sort(byLabel) },
    {
      label: 'Custom areas',
      options: [...customOptions.values()].sort(byLabel),
    },
  ];
}

const FILTER_PARAM_BY_KIND: Record<ReportsFilterKind, string> = {
  aor: 'aorId',
  incident: 'incidentId',
  asset: 'assetId',
  custom: 'customReportId',
};

/**
 * Points the Reports URL at one subject. Choosing an asset or custom area keeps
 * the AOR that was selected, which the server falls back to when the asset or
 * drawn area has no AOR of its own.
 */
export function applyReportsFilterToSearchParams(
  params: URLSearchParams,
  selection: Pick<ReportsFilterSelection, 'kind' | 'value'>
): URLSearchParams {
  const keepAor = selection.kind === 'asset' || selection.kind === 'custom';
  for (const [kind, key] of Object.entries(FILTER_PARAM_BY_KIND)) {
    if (kind === 'aor' && keepAor) continue;
    params.delete(key);
  }
  params.set(FILTER_PARAM_BY_KIND[selection.kind], selection.value);
  return params;
}

type UseReportsFilterOptionsArgs = Omit<
  BuildReportsFilterGroupsArgs,
  'aorsLabel'
> & {
  selectedFilter: ReportsFilterSelection | null;
};

export const useReportsFilterOptions = ({
  aors,
  incidents,
  assets,
  customReports,
  selectedFilter,
}: UseReportsFilterOptionsArgs) => {
  const aorText = useAorText();

  const groupedOptions = useMemo(
    () =>
      buildReportsFilterGroups({
        aors,
        incidents,
        assets,
        customReports,
        aorsLabel: aorText('AORs'),
      }),
    [aors, incidents, assets, customReports, aorText]
  );

  const selectedOption = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }
    return (
      groupedOptions
        .flatMap((group) => group.options)
        .find(
          (option) =>
            option.kind === selectedFilter.kind &&
            option.value === selectedFilter.value
        ) ?? selectedFilter
    );
  }, [groupedOptions, selectedFilter]);

  return {
    groupedOptions,
    selectedOption,
  };
};
